import { PaginatedResponse, Property } from "shared/types";
import { User } from "views/settings-users/types";

import { Notification } from "views/boom-notifications/types";
import { DropDowns } from "./constants";
import { Partner } from "types/landlord/partner";
import { SearchEntities } from "shared/components/Editor/core/extentions/mentions/types";

export enum DueDateOptions {
  NONE = "none",
  TODAY = "today",
  TOMORROW = "tomorrow",
  NEXT_WEEK = "next_week",
  CUSTOM = "custom"
}
export enum CreatedAtOptions {
  NONE = "none",
  TODAY = "today",
  THIS_WEEK = "this_week",
  CUSTOM = "custom"
}
export enum DueDateFilterOptions {
  NONE = "none",
  OVERDUE = "overdue",
  TODAY = "today",
  THIS_WEEK = "this_week",
  CUSTOM = "custom"
}
export enum TaskSubjectPayload {
  BOOM_SCREEN_APPLICATIONS = "application",
  CRM_LEADS = "lead"
}
export enum TaskSubject {
  BOOM_SCREEN_APPLICATIONS = "boom_screen_applications",
  CRM_LEADS = "crm_leads"
}
export enum TaskStatus {
  COMPLETED = "completed",
  INCOMPLETED = "incomplete"
}
export enum SortDirection {
  ASCENDING = "asc",
  DESCENDING = "desc"
}
export enum TaskActivity {
  NOTE = "note_added",
  CREATE_TASK = "task_created",
  CHANGE_TASK_TITLE = "task_title_changed",
  CHANGE_TASK_DESCRIPTION = "task_description_changed",
  CHANGE_TASK_DUE = "task_due_date_changed",
  CHANGE_TASK_SUBJECT = "task_related_subject_changed",
  CHANGE_TASK_ASSIGNEE = "task_assignees_changed",
  COMPLETE_TASK = "task_completed",
  REOPEN_TASK = "task_reopened"
}
export enum SortKey {
  CREATED_AT = "created_at",
  UPDATED_AT = "updated_at",
  STATUS = "status",
  DUE_DATE = "due_date"
}
export enum DefaultOptions {
  ASSIGNEE = "none_assignee",
  RELATED_OBJECT = "none_related_object"
}

type TaskNotifications = Omit<Notification, "rich_text">;
interface ActivityMentionNotification {
  id: number;
  kind: "mention";
  created_at: string;
  type: "mentioned_in_task_notes";
  user: User;
  author?: User | string;
  read_at: string | null;
  url: string;
  updated_at: string;
  rich_text: {
    document_html: string;
    document_text: string;
  };
  metadata: {
    rich_text_id?: number;
    application_id?: number | null;
    note_id?: number;
    task_id?: number | null;
    is_mailed?: boolean;
  };
}
export type DropdownId = (typeof DropDowns)[number];
export type ActivityConfig = {
  label: string;
  iconName: string;
};

export type Subject = {
  id: number;
  name: string;
  property: Property;
  type: TaskSubject;
};
export type SubjectTypeCount = {
  leads_count: number;
  applications_count: number;
};
export type MentionEntity = {
  kind: string;
  subject_type: string;
  subject_id: number;
};
export type RichTextFormat = {
  document_text: string;
  document_html: string;
};
export type RichDoc = RichTextFormat & {
  entities: MentionEntity[];
};
export type TitleResponse = RichTextFormat & {
  notifications: TaskNotifications[];
};

export type ActivityLogRichText = RichTextFormat & {
  notifications: ActivityMentionNotification[];
};
export type WorkflowCreator = {
  id: number;
  type: "Workflow";
  name: string;
};

export type TaskCreatedBy = User | WorkflowCreator;

export type Task = {
  id: number;
  partner: Partner;
  status: TaskStatus;
  subject: Subject | null;
  title: TitleResponse;
  description: RichDoc | null;
  due_date: string;
  created_at: string;
  created_by: TaskCreatedBy;
  updated_at: string;
  completed_at: string | null;
  completed_by: User | null;
  assignees: User[] | null;
  source?: "call_coach" | null;
};

export type TaskSort = Partial<{
  status: SortDirection;
  due_date: SortDirection;
  created_at: SortDirection;
  updated_at: SortDirection;
}>;

import type { Assignee } from "shared/components/Assignee/types";

export type GetTasksParams = {
  page: number;
  per_page: number;
  search?: string;
  statuses: TaskStatus[];
  created_at?: { from: string; to: string };
  due_date?: { from: string; to: string };
  sorts?: TaskSort[];
  creators?: Assignee[];
  subjects?: Array<{ subject_type: TaskSubjectPayload; subject_id?: number }>;
  assignees?: Assignee[];
};
export type relatedTo = {
  subject_type?: TaskSubjectPayload;
  subject_id?: number;
};
export type CreateTaskPayload = {
  title: RichDoc;
  description: RichDoc;
  related_to?: relatedTo;
  due_date?: string;
  assignee_ids?: number[];
};
export type GetCountsBySubjectType = Pick<
  GetTasksParams,
  "created_at" | "statuses" | "assignees" | "creators" | "due_date"
>;

export type DueDateChangedMetadata = {
  original_due_date: string | null;
  changed_due_date: string | null;
};

export type TitleOrDescritionChangedMetadata = {
  original_rich_text: RichTextFormat;
  changed_rich_text: RichTextFormat;
};
export type RelatedSubjectMetaData = {
  subject_type: TaskSubject;
  subject_id: number;
  subject_name: string;
};
export type RelatedSubjectChangedMetadata = {
  original_related_subject: RelatedSubjectMetaData | null;
  changed_related_subject: RelatedSubjectMetaData | null;
};

export type AssigneeChange = {
  assignee_type: SearchEntities;
  assignee_id: number;
  assignee_name: string | null;
};

export type AssigneesChangedMetadata = {
  original_assignees: AssigneeChange[];
  changed_assignees: AssigneeChange[];
};
export interface ActivityNote {
  note_content: string;
  document_html: string;
  entities: MentionEntity[];
}
type ActivityLogMetadata =
  | DueDateChangedMetadata
  | TitleOrDescritionChangedMetadata
  | RelatedSubjectChangedMetadata
  | AssigneesChangedMetadata;

export type ActivityLog = {
  id: number;
  activity_type: TaskActivity;
  title: string;
  description: string | null;
  actor_type: string;
  actor_name: string;
  metadata: ActivityLogMetadata;
  occurred_at: string;
  created_at: string;
  updated_at: string;
  rich_text: ActivityLogRichText | null;
};
export interface NoteData {
  note_content: string;
  document_html: string;
  entities: MentionEntity[];
}
export type ActivityLogsPayload = PaginatedResponse<ActivityLog>;
export type RelatedType = "Lead" | "Rental application";

export type DetailTabData = Omit<Task, "title" | "id" | "partner" | "status">;
